import { createSelector } from 'reselect'

import { CATEGORY_IDS } from '@/constants'
import type { ProductionListSelectorType, ProductionListSelectorWithRowsInfoType } from '@/types/app-types'
import { PageType } from '@/types/app-types'

import type { ProductionsRowsState } from './types'

export const fieldsByType = ({
    pageType,
    productions,
}: {
    pageType: PageType
    productions: ProductionListSelectorType[]
}): ProductionListSelectorWithRowsInfoType[] => {
    // If we end up not needing the AnalyticsProvider for checkout
    // then we can remove this and the PageType.Checkout enum value
    if (pageType == PageType.Checkout) {
        return []
    }
    return productions.map((production: ProductionListSelectorType): ProductionListSelectorWithRowsInfoType => {
        const baseData = {
            href: production.href,
            date: production.date,
            time: production.time,
            day: production.day,
            id: production.id,
            isExternal: production.isExternal,
            analytics: { ...production?.analytics },
            categoryId: production.category,
            venueName: production.venueName,
            location: production.location,
            isSellingFast: production.isSellingFast,
            isLowPricesDealsAvailable: production.isLowPricesDealsAvailable,
        }

        switch (pageType) {
            case PageType.Region:
            case PageType.RegionList:
            case PageType.RegionDetails:
            case PageType.Venue:
                return {
                    label: `${production.venueName} - ${production.location}`,
                    title: production.name,
                    ...baseData,
                }
            case PageType.ConcertsCategory:
            case PageType.Home:
            case PageType.Rewards:
            case PageType.SportsCategory:
            case PageType.TheaterCategory:
            case PageType.Search:
            case PageType.MatchupPage:
            case PageType.CustomPage:
                return {
                    label: production.venueName,
                    title:
                        production.category && production.category === CATEGORY_IDS.SPORTS
                            ? production.name
                            : production.location,
                    sublabel:
                        production.category && production.category === CATEGORY_IDS.SPORTS
                            ? production.location
                            : production.name,
                    ...baseData,
                }
            case PageType.Performer:
                return {
                    label: production.venueName,
                    title:
                        production.category && production.category === CATEGORY_IDS.SPORTS
                            ? production.name
                            : production.location,
                    sublabel:
                        production.category && production.category === CATEGORY_IDS.SPORTS
                            ? production.location
                            : production.name,
                    ticketCount: production.ticketCount,
                    ...baseData,
                }

            default:
                return {} as ProductionListSelectorWithRowsInfoType
        }
    })
}

export const visibleByDefaultProductionsWithFormatSelector = createSelector(
    (state: ProductionsRowsState) => ({ pageType: state.pageType, productions: state.visibleByDefaultProductions }),
    fieldsByType,
)

export const hiddenByDefaultProductionsWithFormatSelector = createSelector(
    (state: ProductionsRowsState) => ({ pageType: state.pageType, productions: state.hiddenByDefaultProductions }),
    fieldsByType,
)

export const showProductionsDataSelector = createSelector(
    visibleByDefaultProductionsWithFormatSelector,
    (productions) => productions.length > 0,
)
